import { BodygramConversionPayload } from "~/server/api/services/bodygram/index.post"
import { Body2FitWidgetInstance, Body2FitOnSupport } from "~/types/bodygram"
import { OrderDetailForThankyouFragment } from "~/types/type.generated"
import { useLocalUser } from "~/composables/useLocalUser"

export const useBody2Fit = () => {
  const config = useRuntimeConfig()
  const { getLocalUserId } = useLocalUser()

  const CLIENT_KEY = config.public.BODY2FIT_CLIENT_KEY || ""
  const BRAND_ID = config.public.BODY2FIT_BRAND_ID || ""
  const localUserId = getLocalUserId()

  /**
   * Body2Fitウィジェットの初期化処理
   * コールバック関数は非同期的な動きをする
   * onSupportは表示に使用するため、Promiseでラップして終了を待つ
   * @param productSlug 商品識別に使用
   */
  const initBody2FitWidget = async (
    productSlug: string,
  ): Promise<{ bodygramWidget: Body2FitWidgetInstance | null; isBodygramSupported: boolean }> => {
    let bodygramWidget: Body2FitWidgetInstance | null = null
    let isBodygramSupported = false

    const initializeWidget = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          isBodygramSupported = false
          reject(
            new Error(
              "Body2Fitウィジェットの初期化がタイムアウトしました。onSupportコールバックが呼び出されませんでした。",
            ),
          )
        }, 10000)

        if (!window.Body2FitWidget) {
          console.error("Body2FitWidgetが見つかりませんでした。スクリプトが正しく読み込まれているか確認してください。")
          isBodygramSupported = false
          clearTimeout(timeoutId)
          resolve()
          return
        }

        try {
          const token = localStorage.getItem("body2fit-token") || ""
          bodygramWidget = new window.Body2FitWidget({
            clientKey: CLIENT_KEY,
            countryCode: "JP",
            languageCode: "ja",
            measurementSystem: "metric",
            productInfo: {
              productId: {
                brandId: BRAND_ID,
                garmentSku: productSlug,
              },
            },
            token,
            disableAddToCart: true,
            enableClearUserData: true,
            clientSessionId: localUserId,
            selfieMode: true,
            onSupport: (event: Body2FitOnSupport) => {
              isBodygramSupported = event.support.supported === "SUPPORTED"
              clearTimeout(timeoutId)
              resolve() // `onSupport` が呼ばれるまで待つ
            },
            onToken: (event: { token: string }) => {
              if (event.token) localStorage.setItem("body2fit-token", event.token)
            },
            onReset: () => {
              localStorage.removeItem("body2fit-token")
            },
          })
        } catch (error) {
          console.error("Body2Fitウィジェットの初期化に失敗しました。設定値を確認してください。", error)
          isBodygramSupported = false
          clearTimeout(timeoutId)
          reject(error)
        }
      })
    }

    try {
      await initializeWidget()
    } catch (error) {
      console.error(error)
    }

    return {
      bodygramWidget,
      isBodygramSupported,
    }
  }

  /**
   * Body2FitウィジェットのCVイベントを送信
   */
  const sendBody2FitConversion = (order: Omit<OrderDetailForThankyouFragment, "_id"> | null): void => {
    if (!order) return
    const item =
      order.items?.map((item) => {
        const product = order.products?.find((product) => product.document_id === item.product_id)
        const productSlug = product?.slug ?? ""
        const size = item.size ?? ""
        const price = item.price ?? 0
        const quantity = 1
        return {
          sku: productSlug,
          brand: BRAND_ID,
          size,
          price,
          quantity,
        }
      }) || []

    const payload: BodygramConversionPayload = {
      clientKey: CLIENT_KEY,
      clientSessionId: localUserId,
      orderId: order.document_id,
      items: item,
      currency: "JPY",
      countryCode: "JP",
      languageCode: "ja",
    }
    useFetch("/api/services/bodygram", {
      method: "POST",
      body: payload,
    }).catch((error) => {
      console.error("Error sending Body2Fit Conversion:", error)
    })
  }

  return { initBody2FitWidget, sendBody2FitConversion }
}
